<script setup>
/**
 * The Journal's ink glyphs (journal redesign 2026-10-27): line icons drawn in
 * currentColor, so each takes the ink of the line it sits on.
 */
import { computed } from 'vue'

const props = defineProps({ name: { type: String, required: true } })

const G = {
  altar: '<path d="M12 3c1.6 2 1.6 3.3 0 4.6-1.6-1.3-1.6-2.6 0-4.6z"/><rect x="9" y="9.5" width="6" height="10" rx="1"/><path d="M5.5 21h13"/>',
  horo: '<circle cx="12" cy="12" r="4"/><path d="M12 2.5v2.3M12 19.2v2.3M2.5 12h2.3M19.2 12h2.3M5.3 5.3l1.6 1.6M17.1 17.1l1.6 1.6M5.3 18.7l1.6-1.6M17.1 6.9l1.6-1.6"/>',
  intent: '<path d="M12 3c3 4 5 6.3 5 10a5 5 0 0 1-10 0c0-2 1-3.6 2-4.6 0 2 1 3 2 3 0-3-1-5.2 1-8.4z"/>',
  paint: '<path d="M14.5 4.5l5 5-7.5 7.5-5-5z"/><path d="M7 12c-2.6.2-3.6 2.2-3.6 4.2 0 1.6-.6 2.6-1.4 3 2.6 1 6 .6 7.2-1.2.9-1.4.6-3-.6-4"/>',
  talk: '<path d="M4 5h16v10.5H9.5L4 20z"/>',
  vote: '<rect x="4" y="10" width="16" height="10" rx="1"/><path d="M8 10V4h8v6"/><path d="M10 7l1.5 1.5L14.2 6"/>',
  lesson: '<path d="M5 19C5 10.5 11 5 20 4c-1 9-6.5 15-15 15z"/><path d="M5 19l8.5-8.5"/>',
  look: '<path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z"/><path d="M18.5 15.5l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7z"/>',
  cal: '<rect x="3.5" y="5" width="17" height="15" rx="2"/><path d="M3.5 9.5h17M8 3v4M16 3v4"/>',
  contents: '<path d="M5 7h14M5 12h14M5 17h14"/>',
  search: '<circle cx="10.5" cy="10.5" r="6"/><path d="M15 15l5.5 5.5"/>',
  more: '<circle cx="5.5" cy="12" r="1.2" fill="currentColor"/><circle cx="12" cy="12" r="1.2" fill="currentColor"/><circle cx="18.5" cy="12" r="1.2" fill="currentColor"/>',
  left: '<path d="M15 5l-7 7 7 7"/>',
  right: '<path d="M9 5l7 7-7 7"/>',
  gift: '<path d="M4.5 11h15v9h-15z"/><path d="M3.5 8h17v3h-17zM12 8v12"/><path d="M12 8c-1.6-3.2-5.2-3.6-5.2-1.2C6.8 8 9.4 8 12 8zm0 0c1.6-3.2 5.2-3.6 5.2-1.2C17.2 8 14.6 8 12 8z"/>',
  star: '<path d="M12 3.5l2.2 6.3 6.3 2.2-6.3 2.2L12 20.5l-2.2-6.3L3.5 12l6.3-2.2z"/>',
  lock: '<rect x="5" y="10.5" width="14" height="10" rx="2"/><path d="M8 10.5V7.5a4 4 0 0 1 8 0v3"/>',
  face: '<path d="M4 8V5.5A1.5 1.5 0 0 1 5.5 4H8M16 4h2.5A1.5 1.5 0 0 1 20 5.5V8M20 16v2.5a1.5 1.5 0 0 1-1.5 1.5H16M8 20H5.5A1.5 1.5 0 0 1 4 18.5V16"/><path d="M9 9.5v1.2M15 9.5v1.2M12 9.5v3.6h-1M9.3 15.6c1.6 1.3 3.8 1.3 5.4 0"/>',
  finger: '<path d="M7 18.5c1.2-2 1.6-4.2 1.6-6.4a3.4 3.4 0 0 1 6.8 0c0 1.4-.1 2.7-.4 4M12 12.2c0 3-1 5.8-2.6 8M15.6 19.2c.7-1.6 1.1-3.3 1.3-5.1M5 15.2c.4-1 .6-2 .6-3.1a6.4 6.4 0 0 1 11.6-3.7M18.8 11.2c.1.3.1.6.1.9"/>',
  moon: '<path d="M30 8a17 17 0 1 0 10 28A14 14 0 1 1 30 8z" stroke-width="1.3"/><path d="M33 15l1 3 3 1-3 1-1 3-1-3-3-1 3-1z" stroke-width="1"/><path d="M38 24l.6 1.6 1.6.6-1.6.6-.6 1.6-.6-1.6-1.6-.6 1.6-.6z" stroke-width="1"/>',
  // Card faces, stand-ins when a card has no picture.
  strength: '<path d="M4 12c0-2.2 1.6-3.4 3.4-3.4 3.4 0 5.2 6.8 9.2 6.8 1.8 0 3.4-1.2 3.4-3.4s-1.6-3.4-3.4-3.4c-4 0-5.8 6.8-9.2 6.8C5.6 15.4 4 14.2 4 12z"/>',
  swords: '<path d="M12 3v14M8.5 14.5h7M12 17v4"/>',
  cups: '<path d="M6 5h12c0 5-2.5 8-6 8s-6-3-6-8zM12 13v5M8.5 20h7"/>',
  wands: '<path d="M7 20L16 4M15 3.5l2.5 1.5M9 20l9-16"/>',
  pentacles: '<circle cx="12" cy="12" r="7"/><path d="M12 6.5l1.6 4.6h4.6l-3.8 2.8 1.5 4.6-3.9-2.9-3.9 2.9 1.5-4.6-3.8-2.8h4.6z"/>',
  wheel: '<circle cx="12" cy="13" r="6"/><path d="M12 7v12M6 13h12M8 9l8 8M16 9l-8 8"/>',
}

const svg = computed(() => G[props.name] || G.star)
const box = computed(() => (props.name === 'moon' ? '0 0 48 48' : '0 0 24 24'))
</script>

<template>
  <!-- eslint-disable-next-line vue/no-v-html -- fixed path data from the table above, never user text -->
  <svg class="jg ink" :viewBox="box" aria-hidden="true" focusable="false" v-html="svg"></svg>
</template>

<style scoped>
.jg { fill: none; stroke: currentColor; stroke-width: 1.6; stroke-linecap: round; stroke-linejoin: round; }
</style>
